import debounce from 'lodash/debounce.js';
import computeScrollIntoView from 'compute-scroll-into-view';
import PendoMultiselectOption from './pendo-multiselect-option.vue.js';
import { setStyles, isPx, raf } from '../../utils/dom.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
let uid = 0;
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  components: {
    PendoMultiselectOption
  },
  props: {
    activate: {
      type: Function
    },
    allOptionsSelected: {
      type: Boolean
    },
    allSelectedText: {
      type: String
    },
    computedMenuWidth: {
      type: Number
    },
    deactivate: {
      type: Function
    },
    disabled: {
      type: Boolean
    },
    filteredOptions: {
      type: Array
    },
    flattenedOptions: {
      type: Array
    },
    formatOptionLabel: {
      type: Function
    },
    getOptionLabel: {
      type: Function
    },
    groupDividers: {
      type: Boolean
    },
    groupLabelKey: {
      type: String
    },
    groupOptionsKey: {
      type: String
    },
    groupSelect: {
      type: Boolean
    },
    handleKeydown: {
      type: Function
    },
    hasGroups: {
      type: Boolean
    },
    hideSelectedOptions: {
      type: Boolean
    },
    inputValue: {
      type: String
    },
    isEntireGroupSelected: {
      type: Function
    },
    isFullWidth: {
      type: Boolean
    },
    isOpen: {
      type: Boolean
    },
    isOptionSelected: {
      type: Function
    },
    isPlaceholderVisible: {
      type: Boolean
    },
    isSelectedLabelVisible: {
      type: Boolean
    },
    labelKey: {
      type: String
    },
    limit: {
      type: Number
    },
    limitText: {
      type: Function
    },
    loading: {
      type: Boolean
    },
    max: {
      type: [Number, Boolean]
    },
    maxMenuHeight: {
      type: [String, Number]
    },
    maxMenuWidth: {
      type: [String, Number]
    },
    minMenuWidth: {
      type: [String, Number]
    },
    model: {
      type: Array
    },
    multiple: {
      type: Boolean
    },
    name: {
      type: String
    },
    noResultsText: {
      type: String
    },
    optionHeight: {
      type: Number
    },
    options: {
      type: null
    },
    placeholder: {
      type: [String, Number]
    },
    pointer: {
      type: Number
    },
    pointerSet: {
      type: Function
    },
    removeSelection: {
      type: Function
    },
    scrollConfig: {
      type: Object
    },
    select: {
      type: Function
    },
    selectedLabel: {
      type: [String, Number]
    },
    selectedValue: {
      type: [Object, Number, String, Array]
    },
    selectGroup: {
      type: Function
    },
    shouldScrollToFocusedOption: {
      type: Boolean
    },
    showMaxElements: {
      type: Boolean
    },
    showNoResults: {
      type: Boolean
    },
    showPointer: {
      type: Boolean
    },
    taggable: {
      type: Boolean
    },
    toggleMenu: {
      type: Function
    },
    updateInputValue: {
      type: Function
    },
    valueKey: {
      type: String
    },
    visibleSelectedValues: {
      type: Array
    }
  },
  data() {
    return {
      listOptions: [],
      scrollTop: 0,
      scrollHeight: 0,
      scrollActive: false,
      buffer: 300,
      scheduleOptionsUpdate: null,
      menuHeight: 0
    };
  },
  computed: {
    listWidth() {
      if (this.showStateContainer && this.$el) {
        return `${this.$el.clientWidth}px`;
      }
      return '100%';
    },
    listData() {
      return this.hasGroups ? this.flattenedOptions : this.filteredOptions;
    },
    showNoDataSlot() {
      if (this.taggable && this.listData.length) {
        return false;
      }
      return !Array.isArray(this.options) || !this.options.length;
    },
    showNoResultsSlot() {
      if (this.showNoDataSlot) {
        return false;
      }
      if (this.showAllSelectedSlot) {
        return false;
      }
      if (this.showMaxElementsSlot) {
        return false;
      }
      if (this.showNoResults) {
        const hasInputValue = this.inputValue.length;
        const noMatchingOptions = this.listData.length === 0;
        return Boolean(hasInputValue && noMatchingOptions);
      }
      return false;
    },
    showAllSelectedSlot() {
      if (this.multiple && this.hideSelectedOptions) {
        if (this.taggable) {
          return !this.inputValue.length && this.allOptionsSelected;
        }
        return this.allOptionsSelected;
      }
      return false;
    },
    showMaxElementsSlot() {
      if (this.showMaxElements && this.multiple && this.max) {
        return this.model.length === this.max;
      }
      return false;
    },
    showStateContainer() {
      if (this.loading) {
        return false;
      }
      return this.showMaxElementsSlot || this.showAllSelectedSlot || this.showNoResultsSlot || this.showNoDataSlot;
    },
    showOptionsList() {
      if (this.showStateContainer) {
        return false;
      }
      return true;
    },
    optionHeights() {
      const {
        listData
      } = this;
      const sizes = {
        '-1': 0
      };
      let total = 0;
      let current;
      let groupIndex = 0;
      for (let i = 0, l = listData.length; i < l; i++) {
        if (listData[i].type === 'group') {
          if (groupIndex === 0) {
            groupIndex++;
            current = this.optionHeight;
          } else {
            current = this.optionHeight + 16;
          }
        } else if (listData[i].divided) {
          current = this.optionHeight + 16;
        } else {
          current = this.optionHeight;
        }
        total += current;
        sizes[i] = total;
      }
      return sizes;
    }
  },
  watch: {
    pointer(index) {
      if (!this.shouldScrollToFocusedOption) {
        return;
      }
      if (this.hasGroups && index < 2) {
        this.$el.scrollTop = 0;
      } else {
        this.scrollHighlightedOptionIntoView(index);
      }
    },
    model: 'setMenuHeight',
    minMenuWidth: 'setMenuWidth',
    computedMenuWidth: 'setMenuWidth',
    maxMenuWidth: 'setMenuWidth',
    maxMenuHeight: 'setMenuHeight',
    listData() {
      this.updateVisibleOptions();
    }
  },
  created() {
    this.updateScrollStatus = debounce(this.updateScrollStatus, 125, {
      leading: true,
      trailing: true
    });
  },
  mounted: _async(function () {
    const _this = this;
    return _await(_this.$nextTick(), function () {
      _this.setMenuHeight();
      _this.setMenuWidth();
      _this.scheduleOptionsUpdate = raf(_this.updateVisibleOptions);
      _this.updateVisibleOptions();
      _this.ready = true;
    });
  }),
  methods: {
    setMenuWidth() {
      const styles = {};
      if (this.isFullWidth) {
        styles.minWidth = '100%';
        styles.maxWidth = '100%';
      } else {
        styles.minWidth = this.computedMenuWidth || this.minMenuWidth;
        styles.maxWidth = this.maxMenuWidth - 2;
        const aside = document.querySelector('.pendo-multiselect__aside');
        if (aside) {
          const maxAvailableOptionsWidth = styles.maxWidth - aside.clientWidth;
          if (maxAvailableOptionsWidth < styles.minWidth) {
            styles.minWidth = maxAvailableOptionsWidth;
          }
        }
      }
      setStyles(this.$el, styles);
    },
    setMenuHeight() {
      const header = document.querySelector('.pendo-multiselect__header');
      const footer = document.querySelector('.pendo-multiselect__footer');
      let maxHeight = isPx(this.maxMenuHeight) ? parseInt(this.maxMenuHeight) : this.maxMenuHeight;
      if (header) {
        maxHeight -= header.clientHeight;
      }
      if (footer) {
        maxHeight -= footer.clientHeight;
      }
      this.menuHeight = maxHeight;
      setStyles(this.$el, {
        maxHeight
      });
    },
    scrollHighlightedOptionIntoView(index) {
      const elem = this.$el.querySelector(`[data-uid='${index}']`);
      if (!elem) {
        this.$el.scrollTop = this.optionHeights[index - 1];
        return;
      }
      const actions = computeScrollIntoView(elem, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest',
        boundary: this.$el
      });
      actions.forEach(_ref => {
        let {
          top
        } = _ref;
        if (top > this.$el.scrollTop) {
          this.$el.scrollTop = top + 8;
        } else if (this.$el.scrollTop > top) {
          this.$el.scrollTop = top - 8;
        }
      });
    },
    updateScrollStatus() {
      this.scrollActive = false;
    },
    handleScroll(event) {
      const {
        clientHeight,
        scrollTop
      } = event.target;
      if (this.scrollTop !== scrollTop) {
        this.scheduleOptionsUpdate({
          start: scrollTop,
          end: scrollTop + clientHeight
        });
        this.scrollActive = true;
        this.updateScrollStatus();
      }
    },
    updateVisibleOptions: _async(function (scroll) {
      const _this2 = this;
      let startIndex = 0;
      let endIndex = 0;
      let option;
      let data;
      const {
        listData,
        listOptions,
        optionHeight,
        buffer,
        hasGroups,
        optionHeights,
        scrollConfig
      } = _this2;
      const {
        enabled
      } = scrollConfig;
      const count = listData.length;
      if (!scroll) {
        const {
          scrollTop
        } = _this2.$el;
        scroll = {
          start: scrollTop,
          end: scrollTop + _this2.menuHeight
        };
      }
      const beforeListHeight = _this2.$refs.beforeList ? _this2.$refs.beforeList.offsetHeight : 0;
      scroll.start -= beforeListHeight;
      if (count && enabled) {
        scroll.start -= buffer;
        scroll.end += buffer;
        if (hasGroups) {
          let scrollStart;
          let a = 0;
          let b = count - 1;
          let currIndex = Math.floor(count / 2);
          let lastIndex;
          do {
            lastIndex = currIndex;
            scrollStart = optionHeights[currIndex];
            if (scrollStart < scroll.start) {
              a = currIndex;
            } else if (currIndex < count - 1 && optionHeights[currIndex + 1] > scroll.start) {
              b = currIndex;
            }
            currIndex = Math.floor((a + b) / 2);
          } while (currIndex !== lastIndex);
          startIndex = Math.max(currIndex, 0);
          _this2.scrollHeight = optionHeights[count - 1];
          _this2.scrollTop = Math.floor(optionHeights[startIndex - 1]);
          for (endIndex = currIndex; endIndex < count && optionHeights[endIndex] < scroll.end; endIndex++) {
            if (endIndex === -1) {
              endIndex = count - 1;
            } else {
              endIndex++;
              endIndex = Math.min(endIndex, count);
            }
          }
        } else {
          endIndex = Math.min(Math.ceil(scroll.end / optionHeight), count);
          startIndex = Math.max(Math.floor(scroll.start / optionHeight), 0);
          _this2.scrollHeight = count * optionHeight;
          _this2.scrollTop = Math.floor(startIndex * optionHeight);
        }
      } else {
        _this2.scrollTop = 0;
        _this2.scrollHeight = 0;
        startIndex = 0;
        endIndex = count;
      }
      for (let o = 0, i = startIndex; i < endIndex; o++, i++) {
        data = listData[i];
        if (listOptions[o]) {
          option = listOptions[o];
          option.nr.index = i;
          option.data = data;
        } else {
          option = {
            data
          };
          const nonReactive = {
            id: uid++,
            index: i
          };
          Object.defineProperty(option, 'nr', {
            configurable: false,
            value: nonReactive
          });
          listOptions.push(option);
        }
      }
      if (listOptions.length + startIndex > count) {
        listOptions.length = Math.max(count - startIndex, 0);
      }
      return _await();
    })
  }
};

export { script as default };
//# sourceMappingURL=pendo-multiselect-options.vue2.js.map
